/**
 * etkOrdersPdfExport.js
 *
 * Generates and downloads a PDF report of ETK Orders.
 * Dependencies: jspdf, jspdf-autotable (installed via npm, bundled with webpack)
 *
 * IMAGE LOADING:
 * Instead of hardcoding base64 strings (fragile, easy to corrupt via
 * copy/paste), we let webpack import the actual PNG files as modules.
 * Webpack's asset/resource (or url-loader/file-loader) handling resolves
 * these to a final URL string at build time, and the browser fetches +
 * converts them to base64 at runtime — no manual encoding involved.
 */

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// These imports resolve to URLs (webpack asset modules) pointing at the
// actual files in src/images/ — adjust the relative path if this file
// doesn't live next to that folder.
import horizontalLogoUrl from "../images/EnvironmentalLabsLogoHorizontal.png";
import iconLogoUrl from "../images/IconLogo.png";

// Simple in-memory cache so we only fetch/convert each image once per page load
const _imageCache = {};

/**
 * jsPDF's addImage needs an actual base64 data URL (or raw base64), not
 * just a URL string. Webpack's asset import gives us a URL (could be a
 * blob:/data: URL in dev, or a real file path after build) — either way,
 * we fetch() it and convert to a base64 data URL once, then cache it.
 */
async function loadImageAsBase64(url) {
    if (_imageCache[url]) return _imageCache[url];

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch image at ${url}: ${response.status}`);
    }
    const blob = await response.blob();

    const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result); // e.g. "data:image/png;base64,iVBORw0KG..."
        reader.onerror = () => reject(new Error(`Failed to read image at ${url}`));
        reader.readAsDataURL(blob);
    });

    _imageCache[url] = dataUrl;
    return dataUrl;
}

// ── Colours ──────────────────────────────────────────────────────────────────
const BRAND_COLOR = [0, 83, 156];
const ACCENT_COLOR = [0, 123, 195];
const LIGHT_ROW = [240, 246, 255];
const TEXT_DARK = [30, 30, 30];
const LINK_COLOR = [0, 90, 180];

/**
 * @param {Object} params
 * @param {Array}  params.ordersData  - Array of order objects
 * @param {string} params.startDate   - "YYYY-MM-DD"
 * @param {string} params.endDate     - "YYYY-MM-DD"
 * @param {string} params.baseUrl     - e.g. "https://scanner.envirolabsinc.com"
 */
export async function generateEtkOrdersPdf({ ordersData, startDate, endDate, baseUrl }) {
    // Load both logos up front so we have real, valid image data before drawing.
    let horizontalLogoDataUrl = null;
    let iconLogoDataUrl = null;
    try {
        [horizontalLogoDataUrl, iconLogoDataUrl] = await Promise.all([
            loadImageAsBase64(horizontalLogoUrl),
            loadImageAsBase64(iconLogoUrl),
        ]);
    } catch (err) {
        console.warn("Logo image(s) failed to load, falling back to text:", err);
    }

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 36;
    const generatedAt = new Date().toLocaleString();

    // ── Page-1 header (no background fill — transparent/white) ──────────────
    const HEADER_H = 68;

    // Horizontal logo — left side
    if (horizontalLogoDataUrl) {
        try {
            doc.addImage(
                horizontalLogoDataUrl,
                "PNG",
                marginX,          // x
                8,                // y
                180,              // width  (pt)
                49,               // height (pt)
            );
        } catch (err) {
            console.warn("Failed to draw horizontal logo:", err);
            drawFallbackTitle(doc, marginX);
        }
    } else {
        drawFallbackTitle(doc, marginX);
    }

    // Report title — centre
    doc.setTextColor(...BRAND_COLOR);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("ETK Orders Report", pageWidth / 2, 30, { align: "center" });

    // Date range + generated info — right side
    doc.setTextColor(...BRAND_COLOR);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    const dateRange = `Date Range: ${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`;
    doc.text(dateRange, pageWidth - marginX, 22, { align: "right" });
    doc.text(`Generated: ${generatedAt}`, pageWidth - marginX, 36, { align: "right" });
    doc.text(`Total Records: ${ordersData.length}`, pageWidth - marginX, 50, { align: "right" });

    // ── Table data ───────────────────────────────────────────────────────────
    const columns = [
        { header: "Order #", dataKey: "orderNumber" },
        { header: "Account #", dataKey: "accountNumber" },
        { header: "Date Received", dataKey: "dateReceived" },
        { header: "Project Description", dataKey: "projectDescription" },
        { header: "Status", dataKey: "orderStatus" },
    ];

    const rows = ordersData.map((o) => ({
        orderNumber: o.orderNumber ?? "",
        accountNumber: o.accountNumber ?? "",
        dateReceived: formatDisplayDate(o.dateReceived),
        projectDescription: o.projectDescription ?? "",
        orderStatus: o.orderStatus ?? "",
    }));

    // Collect clickable link regions populated in didDrawCell
    const linkMap = [];

    autoTable(doc, {
        startY: HEADER_H + 6,
        margin: { left: marginX, right: marginX },
        columns,
        body: rows,
        tableWidth: "auto",

        styles: {
            fontSize: 8,
            cellPadding: { top: 5, bottom: 5, left: 6, right: 6 },
            textColor: TEXT_DARK,
            overflow: "linebreak",
        },

        columnStyles: {
            orderNumber: { cellWidth: 80, textColor: LINK_COLOR, fontStyle: "bold" },
            accountNumber: { cellWidth: 80, textColor: LINK_COLOR, fontStyle: "bold" },
            dateReceived: { cellWidth: 90, halign: "center" },
            projectDescription: { cellWidth: "auto" },
            orderStatus: { cellWidth: 90, halign: "center" },
        },

        headStyles: {
            fillColor: BRAND_COLOR,
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 8.5,
            halign: "center",
        },

        alternateRowStyles: {
            fillColor: LIGHT_ROW,
        },

        // Repeat table header on every page; do NOT re-draw the banner
        showHead: "everyPage",

        didDrawCell(data) {
            if (data.section !== "body") return;
            const { column, row, cell } = data;
            const key = column.dataKey;
            if (key !== "orderNumber" && key !== "accountNumber") return;
            const rawVal = rows[row.index]?.[key];
            if (!rawVal) return;
            const url =
                key === "orderNumber"
                    ? `${baseUrl}/vlp/edit/lab-orders/${rawVal}`
                    : `${baseUrl}/vlp/edit-client/${rawVal}`;
            linkMap.push({ x: cell.x, y: cell.y, w: cell.width, h: cell.height, url });
        },

        // Footer on EVERY page (icon + page numbers)
        didDrawPage(data) {
            const pageCount = doc.internal.getNumberOfPages();
            const currentPage = doc.internal.getCurrentPageInfo().pageNumber;

            // Thin accent line
            doc.setDrawColor(...ACCENT_COLOR);
            doc.setLineWidth(0.5);
            doc.line(marginX, pageHeight - 24, pageWidth - marginX, pageHeight - 24);

            // Small icon logo in footer — left
            if (iconLogoDataUrl) {
                try {
                    doc.addImage(
                        iconLogoDataUrl,
                        "PNG",
                        marginX,         // x
                        pageHeight - 20, // y
                        16,              // width (pt)
                        16,              // height (pt)
                    );
                } catch (err) {
                    console.warn("Failed to draw icon logo:", err);
                }
            }

            // Footer text — centre
            doc.setFontSize(7.5);
            doc.setTextColor(100, 100, 100);
            doc.setFont("helvetica", "normal");
            doc.text(
                `Page ${currentPage} of ${pageCount}   |   ETK Orders Report   |   ${generatedAt}`,
                pageWidth / 2,
                pageHeight - 10,
                { align: "center" }
            );

            // Website URL — right
            doc.text(
                "scanner.envirolabsinc.com",
                pageWidth - marginX,
                pageHeight - 10,
                { align: "right" }
            );
        },
    });

    // ── Apply clickable links ─────────────────────────────────────────────────
    linkMap.forEach(({ x, y, w, h, url }) => {
        doc.link(x, y, w, h, { url });
    });

    // ── Download ──────────────────────────────────────────────────────────────
    const filename = `ETK_Orders_${startDate}_to_${endDate}.pdf`;
    doc.save(filename);
}

function drawFallbackTitle(doc, marginX) {
    doc.setTextColor(...BRAND_COLOR);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Environmental Laboratories, Inc.", marginX, 38);
}

function formatDisplayDate(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
}